import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', tealLight: '#E0F7FC',
  success: '#10B981', successLight: '#D1FAE5',
  blue: '#3B82F6', blueLight: '#EFF6FF',
  purple: '#8B5CF6', purpleLight: '#F5F3FF',
  grey: '#6B7280', border: '#E5E7EB', bg: '#F8FAFC',
  white: '#FFFFFF', dark: '#1F2937',
};

const GOALS = [
  { key: 'general',  icon: '🎯', label: 'General English',    desc: 'Improve my everyday English' },
  { key: 'ielts',    icon: '📝', label: 'IELTS Preparation',  desc: 'Prepare for the IELTS exam' },
  { key: 'business', icon: '💼', label: 'Business English',   desc: 'Professional communication' },
  { key: 'unsure',   icon: '🎓', label: 'Not sure yet',       desc: 'Help me find the right path' },
];

const PHASES = ['welcome', 'goal', 'test', 'writing', 'results'];

export default function AssessmentPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('welcome');
  const [goal, setGoal] = useState('');

  const [botInfo, setBotInfo] = useState(null);

  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [writingPrompt, setWritingPrompt] = useState('');
  const [writingText, setWritingText] = useState('');
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 min
  const [timerActive, setTimerActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [alreadyDone, setAlreadyDone] = useState(null);
  const timerRef = useRef(null);

  // An enrolled student who has ALREADY completed the assessment shouldn't be
  // asked to take it again — send them to their dashboard. We check LIVE via
  // the API (not the cached login flags) so a just-completed assessment is
  // reflected without needing to log out and back in. New students (no result
  // yet → 404) still take the assessment, so onboarding isn't broken.
  const me = getUser();
  const isStudent = me?.role === 'student';
  const isMockCandidate = !!(me && me.is_mock_candidate);
  const [checkingEnroll, setCheckingEnroll] = useState(isStudent);
  useEffect(() => {
    if (!isStudent) return;
    if (isMockCandidate) { navigate('/my-mock-tests', { replace: true }); return; }
    let active = true;
    api.get('/api/assessment/result')
      .then(() => { if (active) navigate('/my-assessment', { replace: true }); })
      .catch(() => { if (active) setCheckingEnroll(false); });
    return () => { active = false; };
  }, [isStudent, navigate]);

  // Fetch bot info on mount
  useEffect(() => {
    api.get('/api/assessment/bot-link').then(r => setBotInfo(r.data)).catch(() => {});
  }, []);

  // Timer
  useEffect(() => {
    if (timerActive && timeLeft > 0) {
      timerRef.current = setInterval(() => setTimeLeft(t => t - 1), 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [timerActive, timeLeft]);

  const formatTime = s => {
    const m = Math.floor(s / 60), sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const checkinPoll = useRef(null);
  const startTest = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/assessment/questions');
      if (res.data.already_completed) {
        setAlreadyDone(res.data);
        setPhase('already_done');
        return;
      }
      if (checkinPoll.current) { clearInterval(checkinPoll.current); checkinPoll.current = null; }
      setQuestions(res.data.questions || []);
      setWritingPrompt(res.data.writing_prompt?.prompt || '');
      setPhase('test');
      setTimerActive(true);
    } catch (e) {
      if (e.response?.status === 403 && e.response?.data?.error === 'waiting_for_checkin') {
        setPhase('waiting_checkin');
        if (!checkinPoll.current) {
          checkinPoll.current = setInterval(() => { startTest(); }, 5000);
        }
      } else {
        setError('Could not load assessment. Please refresh and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => () => { if (checkinPoll.current) clearInterval(checkinPoll.current); }, []);

  const handleAnswer = (qId, option) => {
    setAnswers(prev => ({ ...prev, [qId]: option }));
    // Auto-advance after short delay
    setTimeout(() => {
      if (currentQ < questions.length - 1) {
        setCurrentQ(c => c + 1);
      } else {
        setPhase('writing');
        setTimerActive(false);
      }
    }, 400);
  };

  const submitAssessment = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/api/assessment/submit', {
        answers,
        writing_text: writingText,
        learning_goal: goal,
        phone_number: "",
      });
      setResults(res.data);
      setPhase('results');
    } catch (e) {
      setError('Something went wrong submitting your assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  // Don't flash the welcome screen while we check the student's status.
  if (checkingEnroll) return null;

  // ── Waiting for staff check-in (in-person assessment) ─────────────────────
  if (phase === 'waiting_checkin') {
    return (
      <Screen>
        <Card style={{ textAlign: 'center', maxWidth: 460 }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>⏳</div>
          <h2 style={{ color: C.navy, margin: '0 0 8px' }}>Almost ready</h2>
          <p style={{ color: C.grey, lineHeight: 1.7, margin: '0 0 16px' }}>
            Please wait — a staff member will check you in, and your assessment will start automatically. Keep this page open.
          </p>
          <div style={{ width: 40, height: 40, margin: '0 auto', border: `4px solid ${C.border}`, borderTopColor: C.teal, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </Card>
      </Screen>
    );
  }

  // ── Already done ──────────────────────────────────────────────────────────
  if (phase === 'already_done') {
    return (
      <Screen>
        <Card style={{ textAlign: 'center', maxWidth: 480 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <h2 style={{ color: C.navy, margin: '0 0 8px' }}>Assessment Already Completed</h2>
          <p style={{ color: C.grey, marginBottom: 24 }}>
            You completed your assessment {alreadyDone?.days_ago} day{alreadyDone?.days_ago !== 1 ? 's' : ''} ago.
            You can retake in <strong>{alreadyDone?.can_retake_in_days} days</strong>.
          </p>
          <p style={{ color: C.grey, marginBottom: 24, fontSize: 14 }}>
            Your current level: <strong style={{ color: C.teal }}>{alreadyDone?.cefr_level}</strong>
          </p>
          <Btn onClick={() => navigate('/assessment/result')}>View My Results →</Btn>
          <TextBtn onClick={() => navigate('/dashboard')} style={{ display: 'block', marginTop: 12 }}>
            Go to Dashboard
          </TextBtn>
        </Card>
      </Screen>
    );
  }

  // ── Welcome ───────────────────────────────────────────────────────────────
  if (phase === 'welcome') {
    return (
      <Screen>
        <Card style={{ maxWidth: 560 }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ fontSize: 56, marginBottom: 12 }}>📊</div>
            <h1 style={{ color: C.navy, margin: '0 0 12px', fontSize: 26, fontWeight: 800 }}>
              Your Free English Assessment
            </h1>
            <p style={{ color: C.grey, lineHeight: 1.7, margin: 0, fontSize: 15 }}>
              Welcome! This takes about <strong>15–20 minutes</strong>. We will ask you 20 questions
              to find your level and create your personal learning plan.
              <br /><br />
              <span style={{ color: C.teal, fontWeight: 600 }}>There are no wrong answers — just do your best!</span>
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
            {[
              { icon: '📝', label: '20 Questions', sub: 'Grammar, vocabulary & reading' },
              { icon: '✍️', label: '1 Writing Task', sub: 'Stored for your tutor' },
              { icon: '⚡', label: 'Instant Results', sub: 'Your level in seconds' },
              { icon: '🗺️', label: 'Personal Roadmap', sub: 'Your path to fluency' },
            ].map(f => (
              <div key={f.label} style={{ background: C.bg, borderRadius: 10, padding: '12px 14px', border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>{f.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.navy }}>{f.label}</div>
                <div style={{ fontSize: 12, color: C.grey }}>{f.sub}</div>
              </div>
            ))}
          </div>
          {error && <ErrorBox>{error}</ErrorBox>}
          <Btn onClick={() => setPhase('goal')} disabled={loading}>
            {loading ? 'Loading…' : 'Start My Assessment →'}
          </Btn>
        </Card>
      </Screen>
    );
  }

  // ── Goal selection ────────────────────────────────────────────────────────
  if (phase === 'goal') {
    return (
      <Screen>
        <Card style={{ maxWidth: 540 }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <h2 style={{ color: C.navy, margin: '0 0 8px', fontSize: 22, fontWeight: 800 }}>
              What is your main goal?
            </h2>
            <p style={{ color: C.grey, margin: 0, fontSize: 14 }}>
              This helps us personalise your results and recommend the right programme.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
            {GOALS.map(g => (
              <button key={g.key} onClick={() => { setGoal(g.key); startTest(); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  padding: '16px 18px', borderRadius: 12, cursor: 'pointer',
                  border: goal === g.key ? `2px solid ${C.teal}` : `1px solid ${C.border}`,
                  background: goal === g.key ? C.tealLight : C.white,
                  textAlign: 'left', transition: 'all 0.15s',
                }}>
                <span style={{ fontSize: 28 }}>{g.icon}</span>
                <div>
                  <div style={{ fontWeight: 700, color: C.navy, fontSize: 15 }}>{g.label}</div>
                  <div style={{ fontSize: 13, color: C.grey }}>{g.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </Card>
      </Screen>
    );
  }

  // ── Phone verification ────────────────────────────────────────────────────
  // ── Questions ─────────────────────────────────────────────────────────────
  if (phase === 'test') {
    const q = questions[currentQ];
    const answeredCount = Object.keys(answers).length;
    const progress = ((currentQ) / questions.length) * 100;

    return (
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ fontWeight: 700, color: C.navy, fontSize: 15 }}>📊 English Assessment</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: 13, color: C.grey }}>
              Question <strong style={{ color: C.navy }}>{currentQ + 1}</strong> of {questions.length}
            </div>
            <div style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600,
              background: timeLeft < 300 ? '#FEF2F2' : C.tealLight,
              color: timeLeft < 300 ? '#EF4444' : C.teal,
            }}>
              ⏱ {formatTime(timeLeft)} remaining
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ height: 4, background: C.border }}>
          <div style={{ height: '100%', background: C.teal, width: `${progress}%`, transition: 'width 0.4s ease' }} />
        </div>

        {/* Question */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
          <div style={{ width: '100%', maxWidth: 580 }}>
            <div style={{ background: C.white, borderRadius: 16, padding: '28px 28px 24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 12, color: C.teal, fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
                {q?.lesson_type?.replace('_', ' ') || 'Question'}
              </div>
              <h2 style={{ margin: '0 0 24px', fontSize: 19, fontWeight: 700, color: C.navy, lineHeight: 1.5 }}>
                {q?.question}
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(q?.options || []).map((opt, i) => {
                  const isSelected = answers[q.id] === opt;
                  return (
                    <button key={i} onClick={() => handleAnswer(q.id, opt)}
                      style={{
                        padding: '14px 18px', borderRadius: 10, textAlign: 'left', cursor: 'pointer',
                        border: isSelected ? `2px solid ${C.teal}` : `1px solid ${C.border}`,
                        background: isSelected ? C.tealLight : C.white,
                        color: isSelected ? C.navy : C.dark,
                        fontSize: 15, fontWeight: isSelected ? 700 : 400,
                        transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 12,
                      }}>
                      <span style={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700,
                        background: isSelected ? C.teal : '#F1F5F9',
                        color: isSelected ? C.white : C.grey,
                      }}>
                        {String.fromCharCode(65 + i)}
                      </span>
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Question dots */}
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 20, flexWrap: 'wrap' }}>
              {questions.map((_, i) => (
                <div key={i} onClick={() => setCurrentQ(i)} style={{
                  width: 10, height: 10, borderRadius: '50%', cursor: 'pointer',
                  background: i === currentQ ? C.teal : answers[questions[i]?.id] ? C.success : C.border,
                  transition: 'background 0.2s',
                }} />
              ))}
            </div>

            {currentQ === questions.length - 1 && answers[q?.id] && (
              <Btn onClick={() => setPhase('writing')} style={{ marginTop: 20, width: '100%' }}>
                Continue to Writing Task →
              </Btn>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Writing task ──────────────────────────────────────────────────────────
  if (phase === 'writing') {
    const wordCount = writingText.trim().split(/\s+/).filter(Boolean).length;
    return (
      <Screen>
        <Card style={{ maxWidth: 580 }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: C.purple, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
              ✍️ Writing Task — Final Step
            </div>
            <h2 style={{ margin: '0 0 8px', color: C.navy, fontSize: 20, fontWeight: 800 }}>
              Write a short response
            </h2>
            <p style={{ margin: '0 0 16px', color: C.grey, fontSize: 14, lineHeight: 1.6 }}>
              Write <strong>3–5 sentences</strong>. There is no right or wrong answer —
              your tutor will read this personally after you enrol.
            </p>
            <div style={{ background: C.blueLight, border: `1px solid #BFDBFE`, borderRadius: 10, padding: '14px 16px', fontSize: 14, color: C.navy, marginBottom: 16, lineHeight: 1.6 }}>
              <strong>Prompt:</strong> {writingPrompt || 'Write a few sentences about yourself, your daily routine, and why you want to improve your English.'}
            </div>
            <textarea value={writingText} onChange={e => setWritingText(e.target.value)}
              placeholder="Write your response here…"
              rows={6}
              style={{ width: '100%', padding: '14px', borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 15, lineHeight: 1.7, resize: 'vertical', fontFamily: 'inherit', color: C.dark, outline: 'none', boxSizing: 'border-box' }} />
            <div style={{ textAlign: 'right', fontSize: 12, color: wordCount >= 20 ? C.success : C.grey, marginTop: 4 }}>
              {wordCount} words {wordCount >= 20 ? '✓' : '(aim for 20+)'}
            </div>
          </div>
          <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#92400E', marginBottom: 20 }}>
            📌 Your writing will be saved for your tutor to review. It is <strong>not</strong> graded automatically.
          </div>
          {error && <ErrorBox>{error}</ErrorBox>}
          <Btn onClick={submitAssessment} disabled={loading}>
            {loading ? 'Calculating your results…' : 'See My Results →'}
          </Btn>
          <TextBtn onClick={submitAssessment} style={{ display: 'block', marginTop: 10, textAlign: 'center', fontSize: 12 }}>
            Skip writing task and see results
          </TextBtn>
        </Card>
      </Screen>
    );
  }

  // ── Results ───────────────────────────────────────────────────────────────
  if (phase === 'results' && results) {
    return <AssessmentResults results={results} goal={goal} navigate={navigate} />;
  }

  return (
    <Screen>
      <Card style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 40 }}>⏳</div>
        <p style={{ color: C.grey }}>Loading your assessment…</p>
      </Card>
    </Screen>
  );
}

// ── Results component ─────────────────────────────────────────────────────────

function AssessmentResults({ results, goal, navigate }) {
  const levelColors = {
    A1: C.success, A2: C.success,
    B1: C.blue,    B2: C.blue,
    C1: C.purple,  C2: C.purple,
  };
  const levelBg = {
    A1: '#D1FAE5', A2: '#D1FAE5',
    B1: '#DBEAFE', B2: '#DBEAFE',
    C1: '#EDE9FE', C2: '#EDE9FE',
  };

  const color = levelColors[results.cefr_level] || C.teal;
  const bg = levelBg[results.cefr_level] || C.tealLight;
  const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

  return (
    <div style={{ minHeight: '100vh', background: C.bg, padding: '24px 16px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Section 1 — Your Level */}
        <Card style={{ textAlign: 'center', border: `2px solid ${color}` }}>
          <div style={{ background: bg, borderRadius: 10, padding: '20px 24px', marginBottom: 20 }}>
            <div style={{ fontSize: 52, fontWeight: 900, color, marginBottom: 4 }}>{results.cefr_level}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.navy }}>{results.level_name}</div>
            <div style={{ fontSize: 14, color: C.grey, marginTop: 6 }}>{results.level_description}</div>
          </div>
          <div style={{ fontSize: 32, fontWeight: 800, color, marginBottom: 4 }}>{results.overall_score}%</div>
          <div style={{ fontSize: 13, color: C.grey, marginBottom: 16 }}>Overall Score</div>
          <p style={{ margin: 0, color: C.dark, fontSize: 15, lineHeight: 1.6, fontWeight: 500 }}>
            {results.motivational_message}
          </p>
        </Card>

        {/* Section 2 — Score by level */}
        <Card>
          <h3 style={{ margin: '0 0 16px', color: C.navy, fontSize: 16, fontWeight: 700 }}>📊 Score by Level</h3>
          {levels.map(level => {
            const score = results.scores_by_level?.[level];
            if (score === undefined) return null;
            return (
              <div key={level} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: C.navy }}>{level}</span>
                  <span style={{ color: score >= 60 ? C.success : C.grey }}>{score}%</span>
                </div>
                <div style={{ height: 8, background: '#F1F5F9', borderRadius: 4 }}>
                  <div style={{ height: '100%', borderRadius: 4, background: score >= 80 ? C.success : score >= 60 ? C.blue : '#D1D5DB', width: `${score}%`, transition: 'width 0.6s ease' }} />
                </div>
              </div>
            );
          })}
        </Card>

        {/* Section 3 — Strengths & Weak Areas */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Card style={{ padding: 16 }}>
            <h4 style={{ margin: '0 0 12px', color: C.success, fontSize: 14, fontWeight: 700 }}>✅ Strengths</h4>
            {results.strengths?.length ? results.strengths.slice(0,4).map((s, i) => (
              <div key={i} style={{ fontSize: 13, color: C.dark, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: C.success }}>●</span> {s.topic} <span style={{ color: C.grey, fontSize: 11 }}>({s.score}%)</span>
              </div>
            )) : <p style={{ fontSize: 13, color: C.grey, margin: 0 }}>Keep practising to build strengths!</p>}
          </Card>
          <Card style={{ padding: 16 }}>
            <h4 style={{ margin: '0 0 12px', color: C.blue, fontSize: 14, fontWeight: 700 }}>📈 To Improve</h4>
            {results.weak_areas?.length ? results.weak_areas.slice(0,4).map((w, i) => (
              <div key={i} style={{ fontSize: 13, color: C.dark, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: C.blue }}>↑</span> {w.topic} <span style={{ color: C.grey, fontSize: 11 }}>({w.score}%)</span>
              </div>
            )) : <p style={{ fontSize: 13, color: C.grey, margin: 0 }}>Great work across all areas!</p>}
          </Card>
        </div>

        {/* Writing saved note */}
        {results.writing_saved && (
          <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 12, padding: '12px 16px', fontSize: 13, color: '#92400E' }}>
            ✍️ <strong>Your writing sample has been saved.</strong> Your tutor will review it personally and provide feedback after you enrol.
          </div>
        )}

        {/* Section 4 — Roadmap */}
        <Card>
          <h3 style={{ margin: '0 0 16px', color: C.navy, fontSize: 16, fontWeight: 700 }}>🗺️ Your Learning Roadmap</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
            {levels.map((level, i) => {
              const isCurrent = level === results.cefr_level;
              const isPast = levels.indexOf(level) < levels.indexOf(results.cefr_level);
              return (
                <React.Fragment key={level}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 54 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 800, fontSize: 13,
                      background: isCurrent ? color : isPast ? C.successLight : '#F1F5F9',
                      color: isCurrent ? C.white : isPast ? C.success : C.grey,
                      border: isCurrent ? `3px solid ${color}` : isPast ? `2px solid ${C.success}` : `2px solid ${C.border}`,
                      boxShadow: isCurrent ? `0 0 0 4px ${bg}` : 'none',
                      animation: isCurrent ? 'pulse 2s infinite' : 'none',
                    }}>
                      {isCurrent ? '★' : isPast ? '✓' : level}
                    </div>
                    <div style={{ fontSize: 10, marginTop: 4, color: isCurrent ? color : C.grey, fontWeight: isCurrent ? 700 : 400 }}>{level}</div>
                  </div>
                  {i < levels.length - 1 && (
                    <div style={{ flex: 1, height: 3, background: isPast ? C.success : C.border, minWidth: 12 }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          {results.next_level && (
            <div style={{ background: C.bg, borderRadius: 10, padding: '12px 16px', fontSize: 14, color: C.navy }}>
              🎯 With daily practice you could reach <strong style={{ color }}>{results.next_level}</strong> in approximately <strong>{results.estimated_weeks} weeks</strong>.
            </div>
          )}
        </Card>

        {/* Section 5 — Recommended Package */}
        <Card style={{ border: `2px solid ${C.teal}`, position: 'relative' }}>
          <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: C.teal, color: C.white, padding: '3px 16px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
            ⭐ RECOMMENDED FOR YOU
          </div>
          <div style={{ marginTop: 8 }}>
            <h3 style={{ margin: '0 0 4px', color: C.navy, fontSize: 18, fontWeight: 800 }}>
              {results.recommended_package?.primary}
            </h3>
            <div style={{ fontSize: 26, fontWeight: 800, color: C.teal, marginBottom: 8 }}>
              ${results.recommended_package?.price}<span style={{ fontSize: 14, fontWeight: 400, color: C.grey }}>/mo</span>
            </div>
            <p style={{ margin: '0 0 20px', color: C.grey, fontSize: 14, lineHeight: 1.6 }}>
              {results.recommended_package?.reason}
            </p>
            <Btn onClick={() => navigate('/subscribe')}>Enrol with This Package →</Btn>
          </div>
        </Card>

        {/* Alternative packages */}
        {results.alternative_packages?.length > 0 && (
          <div>
            <h4 style={{ margin: '0 0 12px', color: C.grey, fontSize: 14, fontWeight: 600 }}>Other options:</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {results.alternative_packages.map((pkg, i) => (
                <Card key={i} style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: C.grey, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase' }}>{pkg.tag}</div>
                  <div style={{ fontWeight: 700, color: C.navy, marginBottom: 2 }}>{pkg.name}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.navy, marginBottom: 4 }}>${pkg.price}<span style={{ fontSize: 12, fontWeight: 400, color: C.grey }}>/mo</span></div>
                  <div style={{ fontSize: 12, color: C.grey }}>{pkg.desc}</div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Section 6 — CTA */}
        <Card style={{ textAlign: 'center', background: C.navy }}>
          <h3 style={{ margin: '0 0 8px', color: C.white, fontSize: 18, fontWeight: 800 }}>
            Ready to start your journey{results.next_level ? ` to ${results.next_level}` : ''}?
          </h3>
          <p style={{ margin: '0 0 20px', color: '#94A3B8', fontSize: 14 }}>
            Choose your package and begin today.
          </p>
          <Btn onClick={() => navigate('/subscribe')} style={{ background: C.teal, marginBottom: 10 }}>
            Enrol with Recommended Package →
          </Btn>
          <br />
          <TextBtn onClick={() => navigate('/programmes')} style={{ color: '#94A3B8' }}>
            See All Packages →
          </TextBtn>
          <br />
          <TextBtn onClick={() => navigate('/dashboard')} style={{ color: '#64748B', fontSize: 12, marginTop: 6 }}>
            Save my results — I will decide later
          </TextBtn>
        </Card>

      </div>
      <style>{`@keyframes pulse { 0%,100%{box-shadow:0 0 0 4px ${bg}} 50%{box-shadow:0 0 0 8px ${bg}} }`}</style>
    </div>
  );
}

// ── Shared UI components ──────────────────────────────────────────────────────

function Screen({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
      {children}
    </div>
  );
}

function Card({ children, style = {} }) {
  return (
    <div style={{ background: C.white, borderRadius: 16, padding: 28, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: `1px solid ${C.border}`, ...style }}>
      {children}
    </div>
  );
}

function Btn({ children, onClick, disabled, style = {} }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ width: '100%', padding: '14px 20px', background: disabled ? C.border : C.teal, color: disabled ? C.grey : C.white, border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.15s', ...style }}>
      {children}
    </button>
  );
}

function TextBtn({ children, onClick, style = {} }) {
  return (
    <button onClick={onClick}
      style={{ background: 'none', border: 'none', color: C.teal, cursor: 'pointer', fontSize: 14, fontWeight: 600, padding: 0, ...style }}>
      {children}
    </button>
  );
}

function ErrorBox({ children }) {
  return (
    <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#B91C1C', marginTop: 8 }}>
      {children}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: C.dark, marginBottom: 6 };
const inputStyle = { width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 15, color: C.dark, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
